import type { ReactNode } from 'react';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE EDITOR SURFACE FOR EVERY SETTINGS ROW — A MODAL, AS THE MOCKUP.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS WAS A `Sheet` AND IS NOW A `Dialog`, AND THE HISTORY MATTERS.** The 10 Sep round asked
 * for *"a drawer on the side"* — but the report underneath it was that editing was **invisible**,
 * not that it was in the wrong place: each section opened its editor by clicking the row's NAME,
 * styled exactly like the text beside it. The drawer fixed the wrong half. Every one of these
 * editors is a centred modal in the mockup, which is also the right surface for what they are:
 * eight fields and two buttons, opened, decided and dismissed. A drill-in drawer is for a thing you
 * READ — a task, a contract — beside the list you came from.
 *
 * ⚠️ **A wrapper rather than five `Dialog` call sites**, because the reason these sections
 * disagreed in the first place is that each built its own editor. The shape of *what a settings
 * editor looks like* lives here, and a sixth section inherits it instead of inventing a seventh.
 *
 * ⚠️ **The footer is `Dialog`'s own slot, not a row inside the body.** A body-level row scrolls away
 * with the fields; `footer` is pinned, so Save stays reachable in a form that outgrows the window.
 */
export function EditorDialog({
  open,
  title,
  description,
  width,
  saveLabel,
  saveDisabled,
  onDelete,
  extraAction,
  busy = false,
  error = null,
  deleteLabel,
  onClose,
  onSave,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  /**
   * ⚠️ One sentence under the title, as every dialog in the mockup carries. It says what the thing
   * being edited IS — *"a sablon több dolgozóhoz is hozzárendelhető"* — which is the question a form
   * of eight fields cannot answer on its own.
   */
  readonly description?: string;
  /** Defaults to 560px, the mockup's settings-dialog width. A seven-column grid wants more. */
  readonly width?: number | string;
  /** ⚠️ Already translated. Defaults to *Mentés*; the mockup names the verb on a create. */
  readonly saveLabel?: string;
  readonly saveDisabled: boolean;
  /** A destructive action in the footer's left corner — only where the mockup offers one. */
  readonly onDelete?: () => void;
  /** 26 Oct 2026 · a second way to save, beside the primary — *Az összes ilyen … mellőzése*. */
  readonly extraAction?: { readonly label: string; readonly onClick: () => void };
  /** 26 Oct 2026 · a write is in flight: every button waits, so nothing is sent twice. */
  readonly busy?: boolean;
  /**
   * 26 Oct 2026 · the server's refusal, shown IN the dialog — it now stays open until a save
   * lands, so the error belongs under the fields it is about, not on the page behind it.
   */
  readonly error?: string | null;
  readonly deleteLabel?: string;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly children: ReactNode;
}) {
  const t = useT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
      description={description}
      closeLabel={t('common.close')}
      width={width ?? 560}
      footer={
        /* ⚠️ `w-full`: `Dialog`'s footer slot is `justify-end`, so a wrapper that does not claim the
            width cannot push a destructive action to the other corner. */
        <div className="flex w-full items-center gap-2">
          {onDelete === undefined ? null : (
            <Button variant="ghost" className="text-destructive" onClick={onDelete}>
              {deleteLabel ?? t('common.delete')}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {extraAction === undefined ? null : (
            <Button variant="outline" disabled={saveDisabled || busy} onClick={extraAction.onClick}>
              {extraAction.label}
            </Button>
          )}
          <Button disabled={saveDisabled || busy} onClick={onSave}>
            {saveLabel ?? t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {error === null ? null : (
          <p role="alert" className="text-destructive text-[12px]">
            {error}
          </p>
        )}
        {children}
      </div>
    </Dialog>
  );
}
